import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSearch } from "./SearchContext";
import { API_BASE } from "../config/api";

function HeroSection() {
  const { query, setQuery, filters, specialties, setSpecialties } = useSearch();
  const [filteredSpecialties, setFilteredSpecialties] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [showSticky, setShowSticky] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const heroRef = useRef(null);
  const navigate = useNavigate();
  const prefersReduced = useReducedMotion();

  // ===== Fetch specialties =====
  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/Specialities`, {
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        setSpecialties(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err.name !== "AbortError") console.error("Failed to fetch specialties:", err);
      }
    })();
    return () => ctrl.abort();
  }, [setSpecialties]);

  // ===== Autocomplete =====
  useEffect(() => {
    const id = setTimeout(() => {
      const q = (query || "").trim().toLowerCase();
      if (!q) {
        setFilteredSpecialties([]);
        setOpen(false);
        setHighlightIndex(-1);
        return;
      }
      const starts = specialties.filter((s) => (s?.name || "").toLowerCase().startsWith(q));
      const exact = specialties.some((s) => (s?.name || "").toLowerCase() === q);
      const list = !exact ? starts.slice(0, 8) : [];
      setFilteredSpecialties(list);
      setOpen(list.length > 0);
      setHighlightIndex(list.length ? 0 : -1);
    }, 80);
    return () => clearTimeout(id);
  }, [query, specialties]);

  // ===== Sticky search reveal =====
  useEffect(() => {
    const io = new IntersectionObserver(
      ([entry]) => setShowSticky(!entry.isIntersecting),
      { rootMargin: "-40px 0px 0px 0px", threshold: 0.01 }
    );
    if (heroRef.current) io.observe(heroRef.current);
    return () => io.disconnect();
  }, []);

  const buildParams = (qOverride) => {
    const specialtyToSearch = (qOverride ?? query ?? "").trim();
    const queryParamsObject = {};
    if (specialtyToSearch) queryParamsObject.Specialty = specialtyToSearch;
    if (filters.country) queryParamsObject.Country = filters.country;
    if (filters.city) queryParamsObject.City = filters.city;
    if (filters.neighborhood) queryParamsObject.Area = filters.neighborhood;
    if (filters.gender) queryParamsObject.Gender = filters.gender;
    return new URLSearchParams(queryParamsObject).toString().replace(/\+/g, "%20");
  };

  const go = (qOverride) => {
    const qs = buildParams(qOverride);
    navigate(`/Coaches/Search?${qs}`);
    setOpen(false);
  };

  const hasFilters = useMemo(
    () => !!(filters?.country || filters?.city || filters?.neighborhood || filters?.gender),
    [filters]
  );

  const blobAnim = prefersReduced
    ? {}
    : { animate: { y: [0, 16, 0], x: [0, 12, 0] }, transition: { duration: 12, repeat: Infinity, ease: "easeInOut" } };

  const SearchBar = ({ compact = false, id = "main-search" }) => (
    <div
      className="bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 shadow-lg ring-1 ring-sky-400 rounded-2xl p-2 flex items-center gap-2"
      role="search"
    >
      <label htmlFor={id} className="sr-only">Search</label>
      <svg aria-hidden viewBox="0 0 24 24" className="h-5 w-5 mx-2 text-blue-600">
        <path
          fill="currentColor"
          d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 5 1.5-1.5-5-5ZM4 9.5C4 6.46 6.46 4 9.5 4S15 6.46 15 9.5 12.54 15 9.5 15 4 12.54 4 9.5Z"
        />
      </svg>
      <input
        id={id}
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        placeholder="Search fitness, music, beauty, academics…"
        className={`flex-1 bg-transparent placeholder-slate-400 text-slate-900 focus:outline-none ${
          compact ? "text-base py-2" : "text-base sm:text-lg py-2.5"
        }`}
      />
      <button
        onClick={() => go()}
        className="shrink-0 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 transition shadow"
      >
        Search
      </button>
    </div>
  );

  return (
    <section className="relative w-full overflow-visible" ref={heroRef}>
      {/* STRONG BLUE-SKY BACKGROUND */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-300 via-sky-400 to-blue-500" />
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top,_rgba(59,130,246,0.25),transparent_70%)]" />
        <motion.div
          className="absolute top-[-12%] left-[8%] w-[220px] h-[220px] bg-sky-500/40 rounded-full blur-3xl"
          {...blobAnim}
        />
        <motion.div
          className="absolute bottom-[-16%] right-[6%] w-[280px] h-[280px] bg-blue-600/40 rounded-full blur-3xl"
          {...blobAnim}
        />
      </div>

      {/* CONTENT */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="min-h-[24svh] md:min-h-[26svh] lg:min-h-[28svh] flex flex-col items-center justify-center text-center py-4">
          <motion.h1
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="text-slate-900 text-[clamp(20px,4vw,36px)] font-extrabold tracking-tight leading-tight max-w-3xl"
          >
            Discover top{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-sky-400">
              experts near you
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.02 }}
            className="mt-1 text-slate-800/90 text-[clamp(13px,2.2vw,18px)] max-w-3xl"
          >
            12+ categories • Fitness, Languages, Academics, Music & more
          </motion.p>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
            className="relative z-50 w-full max-w-xl mt-3"
          >
            <SearchBar />

            <AnimatePresence>
              {open && filteredSpecialties.length > 0 && (
                <motion.ul
                  id="suggestions"
                  role="listbox"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-0 right-0 top-full mt-2 rounded-xl bg-white shadow-2xl ring-1 ring-sky-200 text-left z-50"
                  ref={listRef}
                >
                  {filteredSpecialties.map((item, i) => (
                    <li
                      key={`${item?.id ?? i}-${item?.name ?? i}`}
                      className={`px-4 py-3 cursor-pointer border-b last:border-b-0 transition ${
                        i === highlightIndex ? "bg-sky-50" : "hover:bg-sky-50"
                      }`}
                      onMouseEnter={() => setHighlightIndex(i)}
                      onClick={() => go(item?.name)}
                    >
                      {item?.name}
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>

            {hasFilters && (
              <div className="mt-1 text-xs text-slate-700/80">
                Filters:
                {filters.country ? ` ${filters.country}` : ""}
                {filters.city ? ` · ${filters.city}` : ""}
                {filters.neighborhood ? ` · ${filters.neighborhood}` : ""}
                {filters.gender ? ` · ${filters.gender}` : ""}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
